import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_FORM = {
  title: '',
  slug: '',
  issue_date: '',
  subtitle: '',
  issue_theme: '',
  volume_number: '',
  issue_number: '',
  page_count: 40,
  page_count_preset: 'standard',
  cover_image_url: '',
  canonical_cover_asset_url: '',
  cover_thumbnail_url: '',
  fliphtml5_embed_url: '',
  flip_html_url: '',
  flip_reader_type: 'page_sequence',
  download_pdf_url: '',
  member_pdf_url: '',
  editors_note: '',
  editors_note_author: '',
  inside_front_cover_sponsor: '',
  inside_back_cover_sponsor: '',
  back_cover_type: 'house',
  issue_status: 'draft',
  public_access_level: 'public_preview',
  is_latest: false,
  layout_version: '',
  production_notes: '',
};

export const MagazineEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) { navigate('/admin/login'); return; }
    if (isEdit) loadIssue();
  }, [isEdit, navigate]);

  const loadIssue = async () => {
    try {
      const res = await axios.get(`${API}/magazine?issue_status=all`);
      const found = res.data.find(i => i.id === id);
      if (found) {
        setForm({
          title: found.title || '',
          slug: found.slug || '',
          issue_date: found.issue_date || '',
          subtitle: found.subtitle || '',
          issue_theme: found.issue_theme || '',
          volume_number: found.volume_number || '',
          issue_number: found.issue_number || '',
          page_count: found.page_count || 40,
          page_count_preset: found.page_count === 32 ? 'light' : found.page_count === 40 ? 'standard' : 'custom',
          cover_image_url: found.cover_image_url || '',
          canonical_cover_asset_url: found.canonical_cover_asset_url || '',
          cover_thumbnail_url: found.cover_thumbnail_url || '',
          fliphtml5_embed_url: found.fliphtml5_embed_url || '',
          flip_html_url: found.flip_html_url || '',
          flip_reader_type: found.flip_reader_type || 'page_sequence',
          download_pdf_url: found.download_pdf_url || '',
          member_pdf_url: found.member_pdf_url || '',
          editors_note: found.editors_note || '',
          editors_note_author: found.editors_note_author || '',
          inside_front_cover_sponsor: found.inside_front_cover_sponsor || '',
          inside_back_cover_sponsor: found.inside_back_cover_sponsor || '',
          back_cover_type: found.back_cover_type || 'house',
          issue_status: found.issue_status || 'draft',
          public_access_level: found.public_access_level || 'public_preview',
          is_latest: found.is_latest || false,
          layout_version: found.layout_version || '',
          production_notes: found.production_notes || '',
        });
      }
    } catch (err) {
      toast.error('Failed to load issue');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePageCountPreset = (preset) => {
    const count = preset === 'standard' ? 40 : preset === 'light' ? 32 : form.page_count;
    setForm(prev => ({ ...prev, page_count_preset: preset, page_count: count }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.issue_date.trim()) {
      toast.error('Title and Issue Date are required.');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        ...form,
        slug: form.slug.trim() || undefined,
        volume_number: form.volume_number ? parseInt(form.volume_number) : null,
        issue_number: form.issue_number ? parseInt(form.issue_number) : null,
        page_count: parseInt(form.page_count) || 40,
      };

      if (isEdit) {
        await axios.put(`${API}/magazine/${id}`, payload, { headers });
        toast.success('Issue updated successfully!');
        navigate(`/admin/magazine/${id}/pages`);
      } else {
        await axios.post(`${API}/magazine`, payload, { headers });
        toast.success('Issue created successfully!');
        navigate('/admin/magazine');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save issue');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="editor-loading">
        <Loader2 className="animate-spin text-[#0E2E59]" size={36} />
      </div>
    );
  }

  return (
    <AdminLayout title="Magazine Manager" breadcrumb={isEdit ? 'Edit Issue' : 'New Issue'}>
    <div className="min-h-screen bg-gray-50" data-testid="magazine-editor-page">
      <div className="container-custom py-8 max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="magazine-editor-form">

          {/* Identity */}
          <Section title="Issue Identity">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Title *" name="title" value={form.title} onChange={handleChange} placeholder="America at 250" required data-testid="title-input" />
              <Field label="Issue Date *" name="issue_date" value={form.issue_date} onChange={handleChange} placeholder="JULY 2026" required data-testid="issue-date-input" />
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Slug <span className="text-gray-400 font-normal text-xs">(URL identifier — e.g. america-at-250)</span>
                </label>
                <input
                  type="text"
                  name="slug"
                  value={form.slug}
                  onChange={handleChange}
                  placeholder="america-at-250"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0E2E59]"
                  data-testid="slug-input"
                />
                {form.slug && (
                  <p className="text-xs text-teal-600 mt-1 font-mono">/magazine/{form.slug}</p>
                )}
              </div>
              <Field label="Subtitle" name="subtitle" value={form.subtitle} onChange={handleChange} placeholder="A landmark issue for a landmark year" data-testid="subtitle-input" />
              <Field label="Issue Theme" name="issue_theme" value={form.issue_theme} onChange={handleChange} placeholder="Ambition, Resilience, and the Future of Work" data-testid="theme-input" />
              <Field label="Volume Number" name="volume_number" type="number" value={form.volume_number} onChange={handleChange} data-testid="volume-input" />
              <Field label="Issue Number" name="issue_number" type="number" value={form.issue_number} onChange={handleChange} data-testid="issue-number-input" />

              {/* Page Count — Standard / Light / Custom */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Issue Size</label>
                <div className="flex gap-2 mb-2" data-testid="page-count-preset">
                  {[
                    { id: 'standard', label: 'Standard (40pp)', count: 40 },
                    { id: 'light',    label: 'Light (32pp)',     count: 32 },
                    { id: 'custom',   label: 'Custom',           count: null },
                  ].map(opt => (
                    <button key={opt.id} type="button"
                      onClick={() => handlePageCountPreset(opt.id)}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold border transition-colors ${
                        form.page_count_preset === opt.id
                          ? 'bg-[#0E2E59] text-white border-[#0E2E59]'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-[#0E2E59]'
                      }`}
                      data-testid={`preset-${opt.id}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {form.page_count_preset === 'custom' && (
                  <input type="number" name="page_count" value={form.page_count} onChange={handleChange}
                    min={8} max={200} placeholder="Enter page count"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2E59]"
                    data-testid="page-count-custom-input" />
                )}
                {form.page_count_preset !== 'custom' && (
                  <p className="text-xs text-gray-400 mt-1">{form.page_count} pages selected</p>
                )}
              </div>

              <Field label="Layout Version" name="layout_version" value={form.layout_version} onChange={handleChange} placeholder="v1" data-testid="layout-version-input" />
            </div>
          </Section>

          {/* Assets */}
          <Section title="Cover & Assets (ImageKit-ready URLs)">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Cover Image URL" name="cover_image_url" value={form.cover_image_url} onChange={handleChange} placeholder="https://ik.imagekit.io/..." data-testid="cover-url-input" />
              <Field label="Canonical Cover Asset URL" name="canonical_cover_asset_url" value={form.canonical_cover_asset_url} onChange={handleChange} placeholder="https://ik.imagekit.io/..." data-testid="canonical-cover-input" />
              <Field label="Cover Thumbnail URL" name="cover_thumbnail_url" value={form.cover_thumbnail_url} onChange={handleChange} data-testid="thumbnail-input" />
              <Field label="Download PDF URL" name="download_pdf_url" value={form.download_pdf_url} onChange={handleChange} data-testid="pdf-url-input" />
              <Field label="Member PDF URL" name="member_pdf_url" value={form.member_pdf_url} onChange={handleChange} data-testid="member-pdf-input" />
            </div>
          </Section>

          {/* Reader */}
          <Section title="Reader Configuration">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Flip Reader Type</label>
                <select name="flip_reader_type" value={form.flip_reader_type} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2E59]"
                  data-testid="reader-type-select">
                  <option value="page_sequence">Page Sequence (Default)</option>
                  <option value="self_hosted">Self-Hosted Flipbook</option>
                  <option value="legacy_iframe">Legacy iFrame (FlipHTML5)</option>
                </select>
              </div>
              <Field label="Flip HTML URL (self-hosted)" name="flip_html_url" value={form.flip_html_url} onChange={handleChange} placeholder="https://magazine.opportunitypost.com/..." data-testid="flip-html-input" />
              <Field label="FlipHTML5 Embed URL (legacy)" name="fliphtml5_embed_url" value={form.fliphtml5_embed_url} onChange={handleChange} placeholder="https://online.fliphtml5.com/..." data-testid="fliphtml5-input" />
            </div>
          </Section>

          {/* Editorial */}
          <Section title="Editorial">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Editor's Note</label>
                <textarea name="editors_note" value={form.editors_note} onChange={handleChange} rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2E59]"
                  placeholder="A message from the editorial desk..."
                  data-testid="editors-note-input" />
              </div>
              <Field label="Editor's Note Author" name="editors_note_author" value={form.editors_note_author} onChange={handleChange} placeholder="The Editorial Team" data-testid="editors-note-author-input" />
            </div>
          </Section>

          {/* Sponsors */}
          <Section title="Fixed Sponsor Placements">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Inside Front Cover Sponsor (p.2)" name="inside_front_cover_sponsor" value={form.inside_front_cover_sponsor} onChange={handleChange} placeholder="Company Name" data-testid="ifc-sponsor-input" />
              <Field label="Inside Back Cover Sponsor (p.39)" name="inside_back_cover_sponsor" value={form.inside_back_cover_sponsor} onChange={handleChange} placeholder="Company Name" data-testid="ibc-sponsor-input" />
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Back Cover Type (p.40)</label>
                <select name="back_cover_type" value={form.back_cover_type} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2E59]"
                  data-testid="back-cover-type-select">
                  <option value="house">House Ad</option>
                  <option value="paid">Paid Advertiser</option>
                  <option value="psa">PSA</option>
                  <option value="signal">Opportunity Signal</option>
                  <option value="civic">Civic</option>
                </select>
              </div>
            </div>
          </Section>

          {/* Status & Access */}
          <Section title="Status & Access">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Issue Status</label>
                <select name="issue_status" value={form.issue_status} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2E59]"
                  data-testid="issue-status-select">
                  <option value="draft">Draft — internal only</option>
                  <option value="review">Under Review — editorial sign-off required</option>
                  <option value="published">Published — live on public site</option>
                  <option value="archived">Archived — removed from public view</option>
                </select>
                {form.issue_status === 'published' && (
                  <p className="text-xs text-green-600 mt-1 font-medium">
                    This issue will appear on the public /magazine page.
                  </p>
                )}
                {form.issue_status === 'draft' || form.issue_status === 'review' ? (
                  <p className="text-xs text-gray-400 mt-1">
                    Draft and Review issues are hidden from public visitors.
                  </p>
                ) : null}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Public Access Level</label>
                <select name="public_access_level" value={form.public_access_level} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2E59]"
                  data-testid="access-level-select">
                  <option value="public_preview">Public Preview</option>
                  <option value="member_only">Members Only</option>
                  <option value="sponsor_preview">Sponsor Preview</option>
                </select>
              </div>
              <div className="col-span-full p-4 rounded-xl border" style={{ borderColor: form.is_latest ? '#D97706' : '#E5E7EB', background: form.is_latest ? '#FFFBEB' : 'transparent' }}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" id="is_latest" name="is_latest" checked={form.is_latest} onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300 text-[#0E2E59] focus:ring-[#0E2E59] mt-0.5"
                    data-testid="is-latest-checkbox" />
                  <div>
                    <label htmlFor="is_latest" className="text-sm font-bold text-gray-800 cursor-pointer">
                      Mark as Latest Issue
                    </label>
                    <p className="text-xs mt-1" style={{ color: form.is_latest ? '#92400E' : '#6B7280' }}>
                      {form.is_latest
                        ? 'This will become the featured issue. The current latest issue will be moved to archive position.'
                        : 'Only one issue can be marked latest at a time. The homepage and /magazine use this flag.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* Notes */}
          <Section title="Production Notes">
            <textarea name="production_notes" value={form.production_notes} onChange={handleChange} rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2E59]"
              placeholder="Internal notes for the production team..."
              data-testid="production-notes-input" />
          </Section>

          {/* Submit */}
          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#0E2E59] text-white px-7 py-3 rounded-lg font-bold hover:bg-[#1a4d8f] transition-colors shadow disabled:opacity-60"
              data-testid="save-issue-btn"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              {saving ? 'Saving...' : isEdit ? 'Update Issue' : 'Create Issue'}
            </button>
            <Link to="/admin/magazine" className="text-sm text-gray-500 hover:text-gray-800 transition-colors" data-testid="cancel-btn">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
    </AdminLayout>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
    <h3 className="text-base font-bold text-[#0E2E59] mb-4 pb-3 border-b border-gray-100">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, name, value, onChange, type = 'text', placeholder, required, ...rest }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2E59]"
      {...rest}
    />
  </div>
);
